import { ImageOff, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { facePageNumber } from '@/components/faces/format'
import { Card, CardContent } from '@/components/ui/card'
import { cn } from '@/utils/cn'
import { DocumentTimestampDetails } from './DocumentTimestampDetails'

export interface IdentityPhotoTile {
  documentId: string
  faceId: string
  /** Blob URL of the complete source image, null while it loads or when missing. */
  thumbnailUrl: string | null
  /** File name of the photo; null falls back to the translated placeholder. */
  documentName: string | null
  /** The face's stored page: the overlay opens on it, `facePageNumber` names it. */
  page: number | null
  uploadedAt?: string | null
  processedAt?: string | null
}

export interface IdentityPhotosGridProps {
  photos: readonly IdentityPhotoTile[]
  loading?: boolean
  /** Opens the photo in the document overlay, focused on that face. */
  onOpen: (photo: IdentityPhotoTile) => void
}

/**
 * Every full photo with a confirmed face of this person (PRD-0031 SC-005).
 *
 * Pure: the tiles are handed their already-resolved blob URLs, so the grid does
 * no fetching. Clicking one opens the shared document overlay focused on the
 * face that put the photo here.
 */
export function IdentityPhotosGrid({ photos, loading = false, onOpen }: IdentityPhotosGridProps) {
  const { t } = useTranslation()

  if (loading && photos.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="size-6 animate-spin text-muted-foreground" aria-hidden />
        </CardContent>
      </Card>
    )
  }

  if (photos.length === 0) {
    return (
      <Card className="bg-card border-border border-dashed">
        <CardContent className="py-8 text-center text-sm text-muted-foreground">
          {t('identity.photos.empty')}
        </CardContent>
      </Card>
    )
  }

  return (
    <Card aria-busy={loading} className={cn('bg-card border-border', loading && 'opacity-60')}>
      <CardContent className="pt-6">
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
          {photos.map(photo => {
            const name = photo.documentName ?? t('identity.photos.unknownDocument')
            const page = facePageNumber(photo.page, photo.documentName)
            return (
              <li key={`${photo.documentId}:${photo.faceId}`}>
                <button
                  type="button"
                  onClick={() => onOpen(photo)}
                  title={name}
                  data-document-id={photo.documentId}
                  data-face-id={photo.faceId}
                  className="group flex w-full flex-col gap-2 overflow-hidden rounded-md border border-border bg-muted/40 p-1 text-left transition-colors hover:border-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <span className="flex aspect-square items-center justify-center overflow-hidden rounded-sm bg-muted">
                    {photo.thumbnailUrl ? (
                      <img
                        src={photo.thumbnailUrl}
                        alt={t('identity.photos.thumbnailAlt', { name })}
                        className="size-full object-contain"
                      />
                    ) : (
                      <ImageOff className="size-6 text-muted-foreground" aria-hidden />
                    )}
                  </span>
                  <span className="truncate px-1 pb-1 text-2xs text-muted-foreground">
                    {page === null ? name : t('identity.photos.pageLabel', { page, name })}
                  </span>
                  {(photo.uploadedAt || photo.processedAt) && (
                    <span className="px-1 pb-1">
                      <DocumentTimestampDetails
                        uploadedAt={photo.uploadedAt}
                        processedAt={photo.processedAt}
                      />
                    </span>
                  )}
                </button>
              </li>
            )
          })}
        </ul>
      </CardContent>
    </Card>
  )
}
